import { Facebook as FacebookIcon, Instagram as InstagramIcon, LinkedIn as LinkedInIcon, Twitter as TwitterIcon } from '@mui/icons-material';
import { Box, Container, Divider, Grid, IconButton, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

export default function Footer() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const currentYear = new Date().getFullYear();

    const footerLinks = [
        {
            title: 'Ürün',
            links: [
                { name: 'Özellikler', url: '/features' },
                { name: 'Fiyatlandırma', url: '/pricing' },
                { name: 'Entegrasyonlar', url: '/integrations' },
                { name: 'SSS', url: '/faq' },
            ]
        },
        {
            title: 'Şirket',
            links: [
                { name: 'Hakkımızda', url: '/about-us' },
                { name: 'Blog', url: '/blog' },
                { name: 'Kariyer', url: '/careers' },
                { name: 'İletişim', url: '/contact' },
            ]
        },
        {
            title: 'Kaynaklar',
            links: [
                { name: 'Dokümantasyon', url: '/docs' },
                { name: 'Destek', url: '/support' },
                { name: 'Topluluk', url: '/community' },
                { name: 'Eğitimler', url: '/tutorials' },
            ]
        },
        {
            title: 'Yasal',
            links: [
                { name: 'Gizlilik Politikası', url: '/privacy-policy' },
                { name: 'Kullanım Koşulları', url: '/terms' },
                { name: 'KVKK', url: '/kvkk' },
                { name: 'Çerez Politikası', url: '/cookie-policy' },
            ]
        }
    ];

    return (
        <Box
            sx={{
                bgcolor: theme.palette.background.light || '#f7f8fa',
                color: theme.palette.text.primary,
                pt: { xs: 8, md: 10 },
                pb: { xs: 4, md: 5 },
                borderTop: `1px solid ${theme.palette.grey[100]}`,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={isMobile ? 4 : 4}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ mb: { xs: 2, md: 4 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary }}>
                                Helpigo
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    mb: 3,
                                    fontSize: { xs: '0.85rem', md: '0.9rem' },
                                    maxWidth: { md: '90%' }
                                }}
                            >
                                Pazaryerleri mağazanızdaki müşteri sorularını yapay zeka ile otomatik yanıtlayarak satışlarınızı artırın ve zamandan tasarruf edin.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        '&:hover': { color: '#1877F2' } // Facebook rengi
                                    }}
                                >
                                    <FacebookIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        '&:hover': { color: '#1DA1F2' } // Twitter rengi
                                    }}
                                >
                                    <TwitterIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        '&:hover': { color: '#E4405F' } // Instagram rengi
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        '&:hover': { color: '#0A66C2' } // LinkedIn rengi
                                    }}
                                >
                                    <LinkedInIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Grid>

                    {footerLinks.map((section, index) => (
                        <Grid item xs={6} sm={3} md={2} key={index}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    mb: 2,
                                    color: theme.palette.text.primary,
                                    fontSize: { xs: '0.9375rem', md: '1rem' }
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                {section.links.map((link, linkIndex) => (
                                    <Box component="li" key={linkIndex} sx={{ mb: 1 }}>
                                        <Link
                                            component={RouterLink}
                                            to={link.url}
                                            color="inherit"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                textDecoration: 'none',
                                                '&:hover': { color: theme.palette.text.primary },
                                                fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                            }}
                                        >
                                            {link.name}
                                        </Link>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: theme.palette.grey[100] }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'center' } }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            textAlign: { xs: 'center', sm: 'left' },
                            fontSize: { xs: '0.8rem', md: '0.85rem' },
                            mb: { xs: 2, sm: 0 }
                        }}
                    >
                        &copy; {currentYear} Helpigo. Tüm hakları saklıdır.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mt: { xs: 0, sm: 0 }, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Link
                            component={RouterLink}
                            to="/privacy-policy"
                            underline="hover"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                '&:hover': { color: theme.palette.text.primary }
                            }}
                        >
                            Gizlilik
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/terms"
                            underline="hover"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                '&:hover': { color: theme.palette.text.primary }
                            }}
                        >
                            Koşullar
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/cookie-policy"
                            underline="hover"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                '&:hover': { color: theme.palette.text.primary }
                            }}
                        >
                            Çerezler
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
} 